import { useMemo } from 'react';
import { normalizeText } from 'src/components/modal/productListModal/highlightKeyword/HighlightKeyword';
import { OptionType } from 'src/components/widget/widget.interface';
import {
  useGetAllProductsWithVariations,
  useGetProductVariationFlavorsWithPagination,
} from 'src/hooks';
import { useGetAllAssemblyTagsWithPagination } from 'src/hooks/api/assemblyTags/useGetAllAssemblyTags';
import { useGetAllComponentTagsWithPagination } from 'src/hooks/api/componentTags/useGetAllComponentTags';
import { useGetLocationOwnerTags } from 'src/hooks/api/locationOwnerTags/useGetLocationOwnerTags';
import { useGetLocationSubZones } from 'src/hooks/api/locationSubZones/useGetLocationSubZones';
import { useGetLocationTagsOnlyWithPagination } from 'src/hooks/api/locationTags/useGetLocationTagsOnly';
import { useGetLocationTimezones } from 'src/hooks/api/locationTimezones/useGetLocationTimezones';
import { useGetLocationZones } from 'src/hooks/api/locationZones/useGetLocationZones';
import { useGetProductCategoriesWithPagination } from 'src/hooks/api/product/productCategories/useGetProductCategories';
import { useGetUsers } from 'src/hooks/api/userActions/useGetUsers';
import {
  IAssemblyTag,
  ILocationTag,
  IProduct,
  IProductCategory,
  IProductVariationFlavor,
} from 'src/interfaces';
import { escapeRegexAndEncodeUriParam, lang } from 'src/utils';

export const useDynamicDropdownFetcher = (
  label: string,
  searchText: string | string[],
) => {
  const queryParams = useMemo(() => {
    const tagQueryParams: string[] = [];
    const tagList = Array.isArray(searchText);
    const searchTerm = !tagList
      ? escapeRegexAndEncodeUriParam(searchText)
      : searchText.map((tag) => escapeRegexAndEncodeUriParam(tag)).join('|');

    tagList && tagQueryParams.push('pageSize=0');

    switch (label) {
      case lang.productCode:
        tagQueryParams.push('sort=product_code');
        searchTerm &&
          tagQueryParams.push(
            `product_code.${!tagList ? 'search' : 'list'}=${searchTerm}`,
          );
        break;

      case lang.flavor:
      case lang.locationOwnerTagsName:
      case lang.ownerOperator.ownerOperatorTags:
      case lang.locationTagsName:
      case lang.locationTags:
      case lang.addTag:
      case lang.assemblyTags:
      case lang.assemblyTagText:
      case lang.componentTags:
      case lang.componentTag:
      case lang.restrictCategoryText:
      case lang.productCategory:
      default:
        tagQueryParams.push('sort=name');
        searchTerm &&
          tagQueryParams.push(
            `${!tagList ? 'name.regexp' : 'id.list'}=${searchTerm}`,
          );
        break;

      // 'None' flavor is represented by an empty string in the database
      // We pull the full list and filter in the 'options' section below
      case 'product_variation_flavor_id':
        tagQueryParams.push('pageSize=0', 'sort=name');
        break;

      case lang.zone:
        tagQueryParams.push('sort=name');
        searchTerm &&
          tagQueryParams.push(`zone${tagList ? '.list' : ''}=${searchTerm}`);
        break;

      case lang.subZone:
        tagQueryParams.push('sort=name');
        searchTerm &&
          tagQueryParams.push(`subzone${tagList ? '.list' : ''}=${searchTerm}`);
        break;

      case lang.timeZone:
        tagQueryParams.push('sort=name');
        searchTerm && tagQueryParams.push(`timezone=${searchTerm}`);
        break;

      case lang.user:
        tagQueryParams.push('sort=user');
        searchTerm && tagQueryParams.push(`user.regexp=${searchTerm}`);
        break;
    }

    return [...tagQueryParams, 'count=true'];
  }, [label, searchText]);

  const getRequest = useMemo(() => {
    switch (label) {
      case lang.productCode:
        return useGetAllProductsWithVariations;
      case lang.flavor:
      case 'product_variation_flavor_id':
        return useGetProductVariationFlavorsWithPagination;
      case lang.timeZone:
        return useGetLocationTimezones;
      case lang.zone:
        return useGetLocationZones;
      case lang.subZone:
        return useGetLocationSubZones;
      case lang.locationOwnerTagsName:
      case lang.ownerOperator.ownerOperatorTags:
        return useGetLocationOwnerTags;
      case lang.locationTagsName:
      case lang.locationTags:
      case lang.addTag:
        return useGetLocationTagsOnlyWithPagination;
      case lang.assemblyTags:
      case lang.assemblyTagText:
        return useGetAllAssemblyTagsWithPagination;
      case lang.componentTags:
      case lang.componentTag:
        return useGetAllComponentTagsWithPagination;
      case lang.user:
        return useGetUsers;
      case lang.restrictCategoryText:
      case lang.productCategory:
        return useGetProductCategoriesWithPagination;
    }
  }, [label]);

  const {
    data: fetchData,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = getRequest?.(queryParams) || {};

  const getNextPage = () => {
    if (hasNextPage) {
      fetchNextPage?.();
    }
  };

  const options: OptionType[] = useMemo(() => {
    if (!fetchData) return [];
    const data = fetchData?.pages.map((page) => page.data).flat();

    switch (label) {
      case lang.assemblyTags:
      case lang.assemblyTagText:
      case lang.componentTags:
      case lang.componentTag:
        return (data as IAssemblyTag[]).map((item) => ({
          label: item.name,
          value: item.id,
        }));

      case lang.productCode:
        return (data as IProduct[]).map((item) => ({
          label: item.product_code,
          value: item.product_code,
        }));

      case lang.restrictCategoryText:
      case lang.productCategory:
        return (data as IProductCategory[]).map((item) => ({
          label: item.name,
          value: item.id,
        }));

      case lang.flavor:
        return (data as IProductVariationFlavor[]).map((item) => ({
          label: item.name,
          value: item.name,
        }));

      // 'None' flavor is represented by an empty string in the database
      // We pull the full list, replace the empty string, and filter
      case 'product_variation_flavor_id':
        const dataWithNoneFlavor = (data as IProductVariationFlavor[]).map(
          (item) => ({
            label: item.name || lang.none,
            value: item.id,
          }),
        );

        // filtering handled in cc-ui for flavor, tagList not used
        const tagList = Array.isArray(searchText);
        const searchTerm = !tagList ? searchText : searchText[0];

        return dataWithNoneFlavor.filter((item) =>
          normalizeText(item.label)?.includes(normalizeText(searchTerm)),
        );

      case lang.zone:
      case lang.subZone:
      case lang.timeZone:
        return (data as string[]).map((item) => ({
          label: item,
          value: item,
        }));

      case lang.user:
        return (data as any[]).map((item) => ({
          label: item.user,
          value: item.user,
        }));

      case lang.locationOwnerTagsName:
      case lang.ownerOperator.ownerOperatorTags:
      case lang.locationTagsName:
      case lang.locationTags:
      case lang.addTag:
        return (data as ILocationTag[]).map((item) => ({
          label: item.name,
          value: item.id,
        }));

      default:
        return [];
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, label, searchText]);

  return {
    options,
    isFetching,
    getNextPage,
  };
};
